import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdLoader } from '@commander-services/gui-components';
import { useQueries } from '@tanstack/react-query';
import {
  TABLE_IMPORT_TEMPLATE,
  TABLE_REFUELINGS_IMPORT,
  TABLE_REFUELINGS_IMPORT_RECORDS,
} from '../../Services/TableService';
import TabComponent from '../TabComponent';
import { tabsByPermission } from '.';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { CmdTable } from '../CmdTable/CmdTable';
import {
  TableSettingsKeys,
  TEMPLATE_ATTACHMENT_KEY,
  TEMPLATE_TABLE_KEY,
  TEMPLATE_TABLE_SETTINGS,
} from './constants';
import { ICmdTableColumn } from '../CmdTable/types';
import FilterChips from '../CmdTable/FilterChips/FilterChips';
import CmdTableSettings from '../CmdTable/settings/CmdTableSettings';
import { CUSTOMERS_KEY, REQUIRED } from '../CmdTable/constants';
import { TTemplateRecords, TTemplateResponse } from './interfaces';
import useTemplate from './useTemplate';
import OnlineHelp from '../OnlineHelp';
import { customersAtom } from '../../store/recoil/customers';
import { arrayOrder, getTableSettings } from '../CmdTable/CmdTableService';
import { columnsAtom, tableFiltersAtomFamily } from '../CmdTable/CmdTableState';

const fetchSize = 50;

export default function ParkingCodebooks() {
  const { formatMessage: f } = useIntl();
  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(TEMPLATE_TABLE_KEY));
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const { columns, columnOrder, columnVisibility } = useTemplate();
  const tabs = React.useState(
    tabsByPermission(
      [TABLE_REFUELINGS_IMPORT_RECORDS, TABLE_REFUELINGS_IMPORT, TABLE_IMPORT_TEMPLATE],
      TABLE_IMPORT_TEMPLATE
    )
  )[0];
  const documentTitle = `${f({ id: 'tab.expense/import-template' })} - ${f({
    id: 'menu.expenseImports',
  })}`;
  useDocumentTitle(documentTitle);

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[]
  ) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ICmdTableColumn[] = [];

    newOrderColumns.map((item) => {
      if (customer.length === 1 && item.name === CUSTOMERS_KEY) {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: true,
        });
      } else {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: item.hidden,
        });
      }
    });

    tempTableSettingOrder.push({ key: 'actions', hidden: false, default: false });

    const orderColumns = arrayOrder([...tempTableSettingOrder], [...tempOrder], 'key');

    setColumnsTable(orderColumns);
  };

  const getSettings = async () => {
    const settings = await getTableSettings(TEMPLATE_TABLE_KEY);
    if (settings) {
      // setTableSettingsState(settings);
      // setSortFilter({ [`${settings.sort.column}`]: settings.sort.direction });
      handleSetOrderColumns(settings.columns);
      // setIsSettingsReady(true);

      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: [`cmdTableSettings-${TEMPLATE_TABLE_KEY}`],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingSettings } = results[0];

  return (
    <div>
      <div className="w-page">
        <div className="w-page-content">
          <div className="page-header">
            <h4 className="page-header__title">
              {`${f({ id: 'menu.expenseImports' })}`}
              <OnlineHelp topic="import-refuelings" />
            </h4>
          </div>
          <div className="container-fluid">
            <TabComponent headerStyles={{ marginTop: 0 }} tabs={tabs} />
            <section className="basic-box border-top-thin border-top-gray">
              <div
                className="basic-box__content"
                // style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}
              >
                <div className="row no-gutters mb-2">
                  <div className="col col-md" />
                  <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
                  <div className="col-auto order-4 order-lg-5">
                    <CmdTableSettings
                      tableId={TEMPLATE_TABLE_KEY}
                      // actionColumnEnabled={true}
                      tableName={TEMPLATE_TABLE_SETTINGS}
                      sort={{ column: TEMPLATE_TABLE_KEY, direction: 'desc' }}
                      translationKeys={TableSettingsKeys}
                    />
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2">
                  {tableFiltersKey &&
                    tableFiltersKey.map((filterId) => (
                      <FilterChips key={filterId} id={filterId} tableName={TEMPLATE_TABLE_KEY} />
                    ))}
                </div>
                {isLoadingSettings && <CmdLoader inContent={true} />}
                <CmdTable<TTemplateResponse, TTemplateRecords>
                  name={TEMPLATE_TABLE_KEY}
                  fetchSize={fetchSize}
                  lockedColumns={[TEMPLATE_ATTACHMENT_KEY]}
                  columns={columns}
                  columnOrder={columnOrder}
                  columnVisibility={columnVisibility}
                />
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
